import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Row,
  Col,
  message,
  Spin,
} from 'antd';
import { CreditCardRequest } from '../../model/Credit-card.model';
import CreditCardService from '../../service/credit-card-service/Credit-card.service';
import BankService from '../../service/bank-service/Bank.service';
import CardTypeService from '../../service/card-type-service/Card-type.service';
import CustomerService from '../../service/customer-service/Customer.service';
import { BankResponse } from '../../model/Bank.model';
import { CardTypeResponse } from '../../model/Card-type.model';
import CustomerResponse from '../../model/Customer.model';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

interface CreateCreditCardModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customerId?: number; // If creating card for specific customer
}

const CreateCreditCardModal: React.FC<CreateCreditCardModalProps> = ({
  visible,
  onClose,
  onSuccess,
  customerId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // State for dropdown options
  const [banks, setBanks] = useState<BankResponse[]>([]);
  const [cardTypes, setCardTypes] = useState<CardTypeResponse[]>([]);
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);

  // Loading states for dropdowns
  const [banksLoading, setBanksLoading] = useState(false);
  const [cardTypesLoading, setCardTypesLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (customerId) {
        form.setFieldsValue({ customerId });
      }
      loadInitialData();
    }
  }, [visible, customerId, form]);

  // Load initial data when modal opens
  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all(
        [
          loadBanks(),
          loadCardTypes(), // Load all card types initially
          !customerId && loadCustomers(), // Only load customers if not pre-selected
        ].filter(Boolean)
      );
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBanks = async () => {
    try {
      setBanksLoading(true);
      const bankList = await BankService.getBankList();
      setBanks(bankList);
    } catch (error) {
      message.error('Không thể tải danh sách ngân hàng');
      console.error('Error loading banks:', error);
    } finally {
      setBanksLoading(false);
    }
  };

  const loadCardTypes = async () => {
    try {
      setCardTypesLoading(true);
      // Always load all card types, not filtered by bank
      const cardTypeList = await CardTypeService.getCardTypeList();
      setCardTypes(cardTypeList);
    } catch (error) {
      message.error('Không thể tải danh sách loại thẻ');
      console.error('Error loading card types:', error);
    } finally {
      setCardTypesLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      setCustomersLoading(true);
      const customerList = await CustomerService.getCustomerList();
      setCustomers(customerList);
    } catch (error) {
      message.error('Không thể tải danh sách khách hàng');
      console.error('Error loading customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitLoading(true);

      const requestData: CreditCardRequest = {
        ...values,
        newStatus: 'DANG_CHO',
        expiryDate: values.expiryDate
          ? dayjs(values.expiryDate).format('YYYY-MM-DD')
          : null,
      };

      let targetCustomerId = customerId || values.customerId;

      await CreditCardService.createCreditCard(targetCustomerId, requestData);
      message.success('Tạo thẻ tín dụng thành công');
      onSuccess();
    } catch (error: any) {
      console.error('Error creating credit card:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Có lỗi xảy ra khi tạo thẻ tín dụng';
      message.error(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Tạo thẻ tín dụng mới"
      visible={visible}
      onCancel={handleCancel}
      width={800}
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Hủy
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitLoading}
          onClick={() => form.submit()}
        >
          Tạo thẻ
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          data-form-type="other"
          initialValues={{
            status: 'LUU_THE',
            defaultFeeRate: 0,
          }}
        >
          <Row gutter={16}>
            {!customerId && (
              <Col span={12}>
                <Form.Item
                  name="customerId"
                  label="Khách hàng"
                  rules={[
                    { required: true, message: 'Vui lòng chọn khách hàng' },
                  ]}
                >
                  <Select
                    placeholder="Chọn khách hàng"
                    showSearch
                    loading={customersLoading}
                    filterOption={(input, option) => {
                      const customer = customers.find(
                        (c) => c.id === option?.value
                      );
                      if (!customer) return false;

                      const searchInput = input.toLowerCase();
                      const name = customer.name?.toLowerCase() || '';
                      const contactInfo =
                        customer.contactInfo?.toLowerCase() || '';
                      const email = customer.email?.toLowerCase() || '';

                      return (
                        name.includes(searchInput) ||
                        contactInfo.includes(searchInput) ||
                        email.includes(searchInput)
                      );
                    }}
                    optionLabelProp="label"
                  >
                    {customers.map((customer) => (
                      <Option
                        key={customer.id}
                        value={customer.id}
                        label={customer.name}
                      >
                        <div>
                          <div className="font-medium">{customer.name}</div>
                          {customer.contactInfo && (
                            <div className="text-sm text-gray-500">
                              Liên hệ: {customer.contactInfo}
                            </div>
                          )}
                          {customer.email && (
                            <div className="text-sm text-gray-500">
                              Email: {customer.email}
                            </div>
                          )}
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}

            <Col span={customerId ? 24 : 12}>
              <Form.Item name="cardNumber" label="Mã số">
                <Input placeholder="Nhập số" data-form-type="other" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="bankId"
                label="Ngân hàng"
                rules={[{ required: true, message: 'Vui lòng chọn ngân hàng' }]}
              >
                <Select
                  placeholder="Chọn ngân hàng"
                  loading={banksLoading}
                  showSearch
                  filterOption={(input, option) => {
                    const bank = banks.find((b) => b.id === option?.value);
                    if (!bank) return false;

                    const searchInput = input.toLowerCase();
                    const name = bank.name?.toLowerCase() || '';

                    return name.includes(searchInput);
                  }}
                  optionLabelProp="label"
                >
                  {banks.map((bank) => (
                    <Option key={bank.id} value={bank.id} label={bank.name}>
                      <div>
                        <div className="font-medium">{bank.name}</div>
                        <div className="text-sm text-gray-500">
                          {bank.address}
                        </div>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="cardTypeId"
                label="Loại thẻ"
                rules={[{ required: true, message: 'Vui lòng chọn loại thẻ' }]}
              >
                <Select
                  placeholder="Chọn loại thẻ"
                  loading={cardTypesLoading}
                  showSearch
                  filterOption={(input, option) => {
                    const cardType = cardTypes.find(
                      (ct) => ct.cardTypeId === option?.value
                    );
                    if (!cardType) return false;

                    const searchInput = input.toLowerCase();
                    const name = cardType.name?.toLowerCase() || '';

                    return name.includes(searchInput);
                  }}
                  optionLabelProp="label"
                >
                  {cardTypes.map((cardType) => (
                    <Option
                      key={cardType.cardTypeId}
                      value={cardType.cardTypeId}
                      label={cardType.name}
                    >
                      <div>
                        <div className="font-medium">{cardType.name}</div>
                        {cardType.bankName && (
                          <div className="text-sm text-gray-500">
                            Ngân hàng: {cardType.bankName}
                          </div>
                        )}
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="cardHolderName" label="Tên chủ thẻ">
                <Input placeholder="Nhập tên chủ thẻ" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="expiryDate" label="Ngày hết hạn">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Chọn ngày hết hạn"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="creditLimit" label="Hạn mức tín dụng (VNĐ)">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Nhập hạn mức"
                  min={0}
                  formatter={(value) =>
                    value
                      ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      : ''
                  }
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="defaultFeeRate" label="Phí mặc định (%)">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Nhập phí mặc định"
                  min={0}
                  max={100}
                  step={0.1}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="status"
                label="Trạng thái thẻ"
                rules={[
                  { required: true, message: 'Vui lòng chọn trạng thái' },
                ]}
              >
                <Select placeholder="Chọn trạng thái">
                  <Option value="TRA_THE">Trả thẻ</Option>
                  <Option value="LUU_THE">Lưu thẻ</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Form.Item name="notes" label="Ghi chú">
                <TextArea
                  rows={3}
                  placeholder="Nhập ghi chú (tùy chọn)"
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateCreditCardModal;
